import React, { useState, useEffect } from "react";
import "../styles/VisitorList.css";
import { checkAndRecordVisit } from "../utils/visitorTracker";

const VisitorList = () => {
  const [visitors, setVisitors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    uniqueIPs: new Set(),
  });

  // Charger les visiteurs depuis localStorage
  useEffect(() => {
    const loadVisitors = () => {
      try {
        const storedVisitors = JSON.parse(localStorage.getItem("visitors") || "[]");
        setVisitors(storedVisitors);

        // Calculer les statistiques
        const today = new Date().toDateString();
        const todayVisits = storedVisitors.filter((v) => {
          const visitDate = new Date(v.timestamp).toDateString();
          return visitDate === today;
        });

        const uniqueIPs = new Set(storedVisitors.map((v) => v.ip));

        setStats({
          total: storedVisitors.length,
          today: todayVisits.length,
          uniqueIPs: uniqueIPs.size,
        });
      } catch (error) {
        console.error("Erreur lors du chargement des visiteurs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVisitors();

    // Enregistrer la visite actuelle si ce n'est pas déjà fait
    const recordCurrentVisit = async () => {
      await checkAndRecordVisit(window.location.pathname);
      loadVisitors(); // Recharger la liste
    };

    recordCurrentVisit();

    // Mettre à jour toutes les 30 secondes pour voir les nouvelles visites
    const interval = setInterval(() => {
      loadVisitors();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fonction pour supprimer un visiteur
  const deleteVisitor = (id) => {
    const updatedVisitors = visitors.filter((v) => v.id !== id);
    localStorage.setItem("visitors", JSON.stringify(updatedVisitors));
    setVisitors(updatedVisitors);
    setStats({
      total: updatedVisitors.length,
      today: updatedVisitors.filter((v) => {
        const today = new Date().toDateString();
        const visitDate = new Date(v.timestamp).toDateString();
        return visitDate === today;
      }).length,
      uniqueIPs: new Set(updatedVisitors.map((v) => v.ip)).size,
    });
  };

  // Fonction pour supprimer tous les visiteurs
  const clearAllVisitors = () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer tous les visiteurs ?")) {
      localStorage.removeItem("visitors");
      localStorage.removeItem("lastVisitTimestamp");
      setVisitors([]);
      setStats({ total: 0, today: 0, uniqueIPs: 0 });
    }
  };

  // Fonction pour exporter les données
  const exportData = () => {
    const dataStr = JSON.stringify(visitors, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `visiteurs_${new Date().toISOString().split("T")[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="visitor-list-loading">Chargement des visiteurs...</div>;
  }

  return (
    <div className="visitor-list-container">
      <div className="visitor-list-header">
        <h2>Liste des Visiteurs</h2>
        <div className="visitor-list-actions">
          <button onClick={exportData} className="btn-export">
            Exporter les données
          </button>
          <button onClick={clearAllVisitors} className="btn-clear">
            Supprimer tout
          </button>
        </div>
      </div>

      <div className="visitor-stats">
        <div className="stat-card">
          <h3>{stats.total}</h3>
          <p>Total des visites</p>
        </div>
        <div className="stat-card">
          <h3>{stats.today}</h3>
          <p>Visites aujourd'hui</p>
        </div>
        <div className="stat-card">
          <h3>{stats.uniqueIPs}</h3>
          <p>Adresses IP uniques</p>
        </div>
      </div>

      {visitors.length === 0 ? (
        <div className="visitor-list-empty">
          <p>Aucun visiteur enregistré pour le moment.</p>
        </div>
      ) : (
        <div className="visitor-list-table-wrapper">
          <table className="visitor-list-table">
            <thead>
              <tr>
                <th>Date/Heure</th>
                <th>Email</th>
                <th>Adresse IP</th>
                <th>Navigateur</th>
                <th>Système</th>
                <th>Page</th>
                <th>Résolution</th>
                <th>Langue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map((visitor) => (
                <tr key={visitor.id}>
                  <td>{visitor.date}</td>
                  <td>{visitor.email || "Non disponible"}</td>
                  <td>{visitor.ip}</td>
                  <td>{visitor.browser}</td>
                  <td>{visitor.os}</td>
                  <td>{visitor.page}</td>
                  <td>{visitor.screenResolution}</td>
                  <td>{visitor.language}</td>
                  <td>
                    <button
                      onClick={() => deleteVisitor(visitor.id)}
                      className="btn-delete"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VisitorList;